/**
 * WDW Calendly-Embed: lädt das Calendly-Widget erst, wenn die Karte in die Nähe des Viewports kommt,
 * und baut den Kalender per Calendly.initInlineWidget ein. Funktioniert auch nach einem Neu-Rendern
 * der Section im Theme-Editor (connectedCallback). Ohne JavaScript bleibt der Fallback-Link sichtbar.
 * Mobil (bis 749 px) passt Calendly die Höhe an den Inhalt an (resize), damit in der Box nicht gescrollt wird.
 */
const WIDGET_SRC = 'https://assets.calendly.com/assets/external/widget.js';

/** @type {Promise<void> | null} */
let widgetPromise = null;

function loadWidget() {
  if (window.Calendly) return Promise.resolve();
  if (!widgetPromise) {
    widgetPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = WIDGET_SRC;
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => {
        widgetPromise = null;
        reject(new Error('Calendly-Widget konnte nicht geladen werden'));
      };
      document.head.append(script);
    });
  }
  return widgetPromise;
}

class WdwCalendlyEmbed extends HTMLElement {
  /** @type {IntersectionObserver | null} */
  #observer = null;

  connectedCallback() {
    if (!this.dataset.url) return;
    if (!('IntersectionObserver' in window)) {
      this.#init();
      return;
    }
    this.#observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          this.#observer?.disconnect();
          this.#init();
        }
      },
      { rootMargin: '600px 0px' }
    );
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
  }

  async #init() {
    const target = this.querySelector('[data-calendly-target]');
    if (!(target instanceof HTMLElement) || target.dataset.loaded) return;
    try {
      await loadWidget();
      target.dataset.loaded = 'true';
      const resize = window.matchMedia('(max-width: 749px)').matches;
      window.Calendly.initInlineWidget({ url: this.dataset.url, parentElement: target, resize });
      this.classList.add('is-loaded');
    } catch (error) {
      console.warn(error);
    }
  }
}

if (!customElements.get('wdw-calendly-embed')) {
  customElements.define('wdw-calendly-embed', WdwCalendlyEmbed);
}
